import { useTranslation } from "@/features/utils/i18n";
import type { JSONSchemaConfig, JSONSchemaProperty, Tool } from "../types";

type ToolRef = Pick<Tool, "name" | "label" | "description">;

/**
 * The tool catalogue in the reader's language.
 *
 * A tool's label, description and form schema come from its backend definition,
 * in English, and stay there as written: the agent and the other consumers read
 * them too. The screens translate them here instead, keyed by the tool's stable
 * `name` and the property path —
 * `tools.catalog.<name>.fields.<prop>[.fields.<sub>].title|description|options.<value>`
 * — and every lookup falls back to the backend text, so a tool or a field the
 * locale files do not know yet still shows up, in English, rather than as a key.
 */
export function useToolCatalog() {
  const { t } = useTranslation("tools");

  // The texts carry literal placeholders ("{{customer_name}}" in the SMS
  // templates' help), which interpolation would blank out.
  const text = (key: string, fallback: string | undefined) =>
    (t(`tools.catalog.${key}`, {
      defaultValue: fallback ?? "",
      skipInterpolation: true,
    }) as string) || undefined;

  const localizeProperty = (
    key: string,
    property: JSONSchemaProperty,
  ): JSONSchemaProperty => {
    const localized: JSONSchemaProperty = {
      ...property,
      title: text(`${key}.title`, property.title),
      description: text(`${key}.description`, property.description),
    };
    if (property.enum) {
      localized.enumLabels = property.enum.map((value, index) =>
        text(
          `${key}.options.${String(value)}`,
          property.enumLabels?.[index] ?? String(value),
        ),
      ) as string[];
    }
    if (property.properties) {
      localized.properties = localizeProperties(key, property.properties);
    }
    if (property.type === "array") {
      localized.addLabel = text(`${key}.add`, undefined);
      // An array's items are described under the array's own key: its rows
      // are what the reader sees, the items schema is only their template.
      if (property.items?.properties) {
        localized.items = {
          ...property.items,
          properties: localizeProperties(key, property.items.properties),
        };
      }
    }
    return localized;
  };

  const localizeProperties = (
    key: string,
    properties: Record<string, JSONSchemaProperty>,
  ) =>
    Object.fromEntries(
      Object.entries(properties).map(([name, property]) => [
        name,
        localizeProperty(`${key}.fields.${name}`, property),
      ]),
    );

  return {
    label: (tool: ToolRef) => text(`${tool.name}.label`, tool.label) ?? "",
    description: (tool: ToolRef) =>
      text(`${tool.name}.description`, tool.description) ?? "",
    schema: (tool: Pick<Tool, "name" | "schema">): JSONSchemaConfig => ({
      ...tool.schema,
      properties: localizeProperties(tool.name, tool.schema.properties ?? {}),
    }),
  };
}
